// Libraries

// Supermove
import {gql} from '@supermove/graphql';
import {useForm, useState} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';

// App
import DisplayValuesForm from '@shared/modules/Billing/forms/DisplayValuesForm';
import UpdateValuesForm from '@shared/modules/Billing/forms/UpdateValuesForm';
import useUpdateValuesMutation from '@shared/modules/Billing/hooks/useUpdateValuesMutation';

// Value updating is pretty messy--there's a form that must be created that isn't actually submitted, but is used to submit via a different form
// Abstracting this logic to a hook so the component using it doesn't have to deal with this
const useUpdateProjectValue = ({
  project,
  refetch,
}: {
  project: ProjectModel;
  refetch: () => void;
}) => {
  const [submitting, setIsSubmitting] = useState(false);
  const displayValuesForm = DisplayValuesForm.edit([project.valueByIdentifier]);
  // Typically the below useForm call would be called in the useMutation function
  // Since we aren't going to be sending this data for a mutation we can do that logic here
  const form = useForm({
    initialValues: {
      displayValuesForm: DisplayValuesForm.toForm(displayValuesForm),
    },
  });
  const {handleSubmit, form: updateValuesForm} = useUpdateValuesMutation({
    updateValuesForm: UpdateValuesForm.edit(project),
    onSuccess: refetch,
    onError: (errors) => console.log({errors}),
  });

  const handleSubmitPress = async () => {
    return new Promise((resolve, reject) => {
      UpdateValuesForm.handleSubmit({
        form: updateValuesForm,
        field: 'updateValuesForm',
        updateValuesObject: {
          [form.values.displayValuesForm.valueForms?.[0]?.valueId]:
            form.values.displayValuesForm.valueForms?.[0],
        },
        // Don't need to handle isSubmitting, use the mutation to handle loading
        setIsSubmitting: () => {},
        handleSubmit: async () => {
          try {
            setIsSubmitting(true);
            const response = await handleSubmit();
            resolve(response);
          } catch (error) {
            reject(error);
          } finally {
            setIsSubmitting(false);
          }
        },
        isEnabledProjectPageAllSections: true,
      });
    });
  };

  const refreshForm = () => {
    const value = project.valueByIdentifier;
    const displayValuesForm = DisplayValuesForm.edit([value]);
    form.resetForm({
      values: {displayValuesForm: DisplayValuesForm.toForm(displayValuesForm)},
    });
  };

  return {
    handleSubmitPress,
    form,
    refreshForm,
    submitting,
  };
};

// --------------------------------------------------
// Data
// --------------------------------------------------

useUpdateProjectValue.fragment = gql`
  ${UpdateValuesForm.edit.fragment}
  ${DisplayValuesForm.edit.fragment}

  fragment useUpdateProjectValue_Value on Value {
    id
    key
    stringValue
    variableIdentifier
    variable {
      id
      variableOptions {
        label
        key
        value
      }
    }
    ...DisplayValuesForm_edit
  }

  fragment useUpdateProjectValue_Project on Project {
    id
    ...UpdateValuesForm_edit
  }
`;

export default useUpdateProjectValue;
